import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter } from "@tanstack/react-router";
import {
  getGalleryTreeApiSessionsGalleryTreeGetQueryKey,
  getMyStatusApiUsersMeGetQueryKey,
  listAssetsApiAssetsGetQueryKey,
  listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey,
  listSessionsApiSessionsGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import {
  getGalleryTreeApiSessionsGalleryTreeGet,
  listAssetsApiAssetsGet,
  listSessionsApiSessionsGet,
} from "../generated/api/sdk.gen";
import type {
  AssetType,
  GalleryTreeResponse,
  SessionListResponse,
} from "../generated/api/types.gen";

export const SESSIONS_PER_PAGE = 20;

/**
 * Invalidate every project's assets list, regardless of which project is
 * selected. A project's sidebar is `union(manually-attached ∪ derived-used)`,
 * and the derived-used set is computed live from the project's designs — so
 * refining a design (which can attach an asset to it) or moving a design
 * between projects changes the list for the affected project(s) without any
 * direct write to `project_assets`. We rarely know the affected project id at
 * the call site (e.g. the refine page only carries a session id), so we match
 * all `listProjectAssets` queries by their generated `_id` and let the active
 * one refetch immediately. Inactive ones are marked stale and refetch on their
 * next mount.
 */
export async function invalidateProjectAssets(
  queryClient: QueryClient,
): Promise<void> {
  const [{ _id }] = listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey({
    path: { project_id: "" },
  });
  await queryClient.invalidateQueries({ queryKey: [{ _id }] });
}

/**
 * Refresh every saved-assets consumer (picker Assets tab, gallery "My
 * Assets" view, modal Existing tabs). The base key omits the `type`
 * filter so it matches all asset list queries via TanStack's partial
 * key matching.
 */
export async function invalidateAssets(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: listAssetsApiAssetsGetQueryKey(),
  });
}

export async function invalidateGallerySessionCaches(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: listSessionsApiSessionsGetQueryKey(),
  });
  await queryClient.invalidateQueries({
    queryKey: getGalleryTreeApiSessionsGalleryTreeGetQueryKey(),
  });
}

export async function invalidateGallerySessions(
  queryClient: QueryClient,
  router: AnyRouter,
): Promise<void> {
  // Both gallery session sources: the paged grid list and the drawer tree.
  // Every generation flow (initial, refinement, selection) lands here, so
  // omitting either one leaves that surface showing pre-generation data
  // until its cache expires — a reload must never be required to see work
  // that already finished.
  await invalidateGallerySessionCaches(queryClient);
  router.invalidate();
}

/**
 * Refresh the iteration allowance after spending one.
 *
 * Without this the count would sit on the global 5-minute staleTime, so the
 * generation that exhausted the allowance would leave the app still believing
 * there was one left — and the out-of-iterations overlay would not appear
 * until the cache happened to expire.
 */
export async function invalidateIterationUsage(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: getMyStatusApiUsersMeGetQueryKey(),
  });
}

/**
 * Active Clerk org id is included in every gallery query key so switching
 * orgs (which changes the JWT and therefore which folders are visible)
 * automatically refetches instead of serving stale cache.
 */
type ActiveOrgId = string | null | undefined;

/**
 * Infinite-scroll query for sessions in a specific gallery scope.
 * Pass `null` for root-only (sessions with no project), a UUID for a project.
 */
export function gallerySessionsByScopeQuery(
  scope: { projectId: string | null },
  orgId: ActiveOrgId,
) {
  // Backend uses the literal "root" sentinel for null project_id.
  const projectIdParam = scope.projectId ?? "root";
  const baseKey = listSessionsApiSessionsGetQueryKey({
    query: { limit: SESSIONS_PER_PAGE, project_id: projectIdParam },
  });

  return {
    queryKey: [...baseKey, orgId ?? null],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const { data } = await listSessionsApiSessionsGet({
        query: {
          limit: SESSIONS_PER_PAGE,
          project_id: projectIdParam,
          offset: pageParam,
        },
      });
      return data;
    },
    getNextPageParam: (
      lastPage: SessionListResponse,
      allPages: SessionListResponse[],
    ) => {
      if (!lastPage.has_more) return;
      return allPages.length * SESSIONS_PER_PAGE;
    },
    initialPageParam: 0,
  };
}

export function galleryTreeQuery(orgId: ActiveOrgId) {
  const baseKey = getGalleryTreeApiSessionsGalleryTreeGetQueryKey();
  return {
    queryKey: [...baseKey, orgId ?? null],
    queryFn: async () => {
      const { data } = await getGalleryTreeApiSessionsGalleryTreeGet();
      return data as GalleryTreeResponse;
    },
  };
}

/**
 * Assets visible to the current user (personal + org-shared when in org
 * context, plus global manufacturer assets), optionally filtered by type.
 * Omit `type` for all assets.
 */
export function assetsQuery(orgId: ActiveOrgId, type?: AssetType) {
  const options = type ? { query: { type } } : undefined;
  const baseKey = listAssetsApiAssetsGetQueryKey(options);
  return {
    queryKey: [...baseKey, orgId ?? null],
    queryFn: async () => {
      const { data } = await listAssetsApiAssetsGet(options);
      return data;
    },
  };
}
